import { toZonedTime } from 'date-fns-tz';
import { format, formatDistance } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const TIMEZONE_BRASILIA = 'America/Sao_Paulo';

/**
 * Retorna a data/hora atual no timezone de Brasília
 */
export function getNowBrasilia(): Date {
  return toZonedTime(new Date(), TIMEZONE_BRASILIA);
}

/**
 * Converte uma data UTC para timezone de Brasília
 */
export function toBrasilia(date: Date | string): Date {
  const d = typeof date === 'string' ? new Date(date) : date;
  return toZonedTime(d, TIMEZONE_BRASILIA);
}

/**
 * Formata data/hora no timezone de Brasília
 */
export function formatBrasilia(date: Date | string, formatStr: string = 'dd/MM/yyyy HH:mm'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const brasiliaDate = toZonedTime(d, TIMEZONE_BRASILIA);
  return format(brasiliaDate, formatStr, { locale: ptBR });
}

// ═══════════════════════════════════════════════════════════════════
// FUNÇÕES PADRONIZADAS DE FORMATAÇÃO DE DATA
// Centralizadas aqui para evitar duplicação e uso de toLocaleDateString
// ═══════════════════════════════════════════════════════════════════

/**
 * Formata data no padrão brasileiro: dd/MM/yyyy
 * @example formatDateBR('2024-01-15') => '15/01/2024'
 */
export function formatDateBR(date: Date | string | null | undefined): string {
  if (!date) return '-';

  // String DATE pura (YYYY-MM-DD): tratar como local para evitar deslocamento UTC
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return format(parseDateLocal(date), 'dd/MM/yyyy', { locale: ptBR });
  }

  const d = typeof date === 'string' ? toBrasilia(date) : date;
  return format(d, 'dd/MM/yyyy', { locale: ptBR });
}

/**
 * Formata data e hora no padrão brasileiro: dd/MM/yyyy às HH:mm
 * @example formatDateTimeBR('2024-01-15T10:30:00') => '15/01/2024 às 10:30'
 */
export function formatDateTimeBR(date: Date | string | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? toBrasilia(date) : date;
  return format(d, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
}

/**
 * Formata data curta: dd/MM
 * @example formatDateShortBR('2024-01-15') => '15/01'
 */
export function formatDateShortBR(date: Date | string | null | undefined): string {
  if (!date) return '-';

  // String DATE pura (YYYY-MM-DD): tratar como local para evitar deslocamento UTC
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return format(parseDateLocal(date), 'dd/MM', { locale: ptBR });
  }

  const d = typeof date === 'string' ? toBrasilia(date) : date;
  return format(d, 'dd/MM', { locale: ptBR });
}

/**
 * Formata data relativa: "há 2 horas", "em 3 dias"
 * @example formatRelativeBR('2024-01-15T10:30:00') => 'há 2 horas'
 */
export function formatRelativeBR(date: Date | string | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return formatDistance(d, new Date(), { addSuffix: true, locale: ptBR });
}

/**
 * Formata data para nome de arquivo: yyyy-MM-dd_HHmm
 * @example formatDateForFilename() => '2024-01-15_1030'
 */
export function formatDateForFilename(date: Date = new Date()): string {
  return format(date, 'yyyy-MM-dd_HHmm');
}

/**
 * Cria uma string ISO com hora 23:59:59 UTC para uma data
 * Útil para datas de prazo/expedição que representam "até o fim do dia"
 * @param dateString - Data no formato YYYY-MM-DD
 */
export function toEndOfDayUTC(dateString: string): string {
  return `${dateString}T23:59:59.999Z`;
}

/**
 * Hoje em 'AAAA-MM-DD', hora de Brasília.
 *
 * É o formato que TODA data-só do sistema usa (production_steps.data_programada,
 * orders.data_prometida, order_occurrences.prazo_entrega) e o valor que um
 * <input type="date"> espera. `toISOString().split('T')[0]` NÃO serve: às 21h
 * de Ribeirão já é o dia seguinte em UTC, e o campo abriria com a data errada.
 */
export function hojeISO(): string {
  return format(getNowBrasilia(), 'yyyy-MM-dd');
}

/**
 * Dia previsto de CONCLUSÃO da etapa: `data_programada_fim`, ou o início quando
 * não há fim (NULL = trabalho de um dia só). 'AAAA-MM-DD' (10 primeiros chars).
 */
export function fimProgramado(step: {
  data_programada?: string | null;
  data_programada_fim?: string | null;
}): string | null {
  const d = step.data_programada_fim ?? step.data_programada;
  return d ? d.slice(0, 10) : null;
}

/**
 * Etapa atrasada = o dia de conclusão previsto já passou (Brasília).
 * Só a data: status/cancelamento/pedido fechado ficam por conta de quem chama.
 */
export function etapaAtrasada(
  step: { data_programada?: string | null; data_programada_fim?: string | null },
  hoje: string = hojeISO(),
): boolean {
  const fim = fimProgramado(step);
  return fim !== null && fim < hoje;
}

/**
 * O instante (timestamp) caiu HOJE no calendário de Brasília?
 *
 * É a regra das colunas "Concluídos" / "Expedidos" de todos os quadros:
 * concluído é o do dia, não o das últimas 24 h (a lista do operador contava
 * 24 h e o quadro contava o dia — as duas formas da mesma tela davam números
 * diferentes). Sem data, `false`: etapa concluída sem `data_fim` não é "de
 * hoje". Compara o dia em Brasília dos DOIS lados, então o fuso do aparelho
 * não muda a resposta.
 */
export function ehHojeBrasilia(instante: Date | string | null | undefined): boolean {
  if (!instante) return false;
  const d = typeof instante === 'string' ? new Date(instante) : instante;
  if (Number.isNaN(d.getTime())) return false;
  return format(toBrasilia(d), 'yyyy-MM-dd') === hojeISO();
}

/**
 * Diferença em DIAS entre hoje e uma data 'AAAA-MM-DD'. Negativo = venceu.
 * Compara dia contra dia (não milissegundos), que é como um prazo se lê.
 */
export function diasAte(dataISO: string | null | undefined): number | null {
  if (!dataISO) return null;
  const alvo = parseDateLocal(dataISO);
  if (Number.isNaN(alvo.getTime())) return null;
  const hoje = parseDateLocal(hojeISO());
  return Math.round((alvo.getTime() - hoje.getTime()) / 86_400_000);
}

/**
 * Converte string DATE (YYYY-MM-DD) para objeto Date no timezone local
 * Evita problema de new Date(string) que interpreta como UTC
 */
export function parseDateLocal(dateString: string): Date {
  if (!dateString) return new Date();
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Formata data do tipo DATE (YYYY-MM-DD) para exibição em pt-BR
 * Usa parseDateLocal para evitar problemas de timezone
 */
export function formatDateLocal(dateString: string): string {
  if (!dateString) return '';
  return parseDateLocal(dateString).toLocaleDateString('pt-BR');
}
